import * as types from '../types';
import { IDispatchApi } from '../contracts';
import { HttpApi } from './HttpApi';

export class DispatchHttpApi extends HttpApi implements IDispatchApi {
  async getDispatchBoard(date: string | undefined, view: types.BoardView): Promise<types.DispatchBoard> {
    const response = await this.http.get<types.APIResponse<types.DispatchBoard>>('/dispatch/board', { params: date ? { date, view } : { view } });
    return response.data.data;
  }

  async getDispatchBacklog(query: { search?: string; limit?: number; after?: string } = {}): Promise<types.DispatchBacklog> {
    const params: Record<string, string | number> = {};
    if (query.search?.trim()) params.search = query.search.trim();
    if (query.limit !== undefined) params.limit = query.limit;
    if (query.after) params.after = query.after;
    const response = await this.http.get<types.APIResponse<types.DispatchBacklog>>('/dispatch/backlog', { params });
    return response.data.data;
  }

  async checkJobSchedule(jobId: string, change: types.ScheduleChange): Promise<types.ScheduleConflict[]> {
    const response = await this.http.post<types.APIResponse<{ conflicts: types.ScheduleConflict[] }>>('/dispatch/check', { jobId, ...change });
    return response.data.data.conflicts;
  }

  async scheduleJob(jobId: string, data: types.ScheduleJobRequest): Promise<types.ScheduleJobResult> {
    const response = await this.http.patch<types.APIResponse<types.ScheduleJobResult>>(`/dispatch/jobs/${jobId}/schedule`, data);
    return response.data.data;
  }

  async getJobSuggestions(request: types.SuggestionRequest): Promise<types.SuggestionResult> {
    const response = await this.http.post<types.APIResponse<types.SuggestionResult>>('/dispatch/suggestions', request);
    return response.data.data;
  }

  async getBulkSuggestions(request: types.BulkSuggestionRequest): Promise<types.BulkSuggestionResult> {
    const response = await this.http.post<types.APIResponse<types.BulkSuggestionResult>>('/dispatch/suggestions/bulk', request);
    return response.data.data;
  }

  async optimizeDay(request: types.OptimizeDayRequest): Promise<types.OptimizeDayResult> {
    const response = await this.http.post<types.APIResponse<types.OptimizeDayResult>>('/dispatch/optimize-day', request);
    return response.data.data;
  }

  async applyDayOptimization(request: types.ApplyDayOptimizationRequest): Promise<types.ApplyDayOptimizationResult> {
    const response = await this.http.post<types.APIResponse<types.ApplyDayOptimizationResult>>('/dispatch/optimize-day/apply', request);
    return response.data.data;
  }

  async getTechnicianWorkHours(userId: string): Promise<types.TechnicianWorkHours> {
    const response = await this.http.get<types.APIResponse<types.TechnicianWorkHours>>(`/dispatch/technicians/${userId}/work-hours`);
    return response.data.data;
  }

  async setTechnicianWorkHours(userId: string, hours: types.WorkHours): Promise<types.TechnicianWorkHours> {
    const response = await this.http.put<types.APIResponse<types.TechnicianWorkHours>>(`/dispatch/technicians/${userId}/work-hours`, hours);
    return response.data.data;
  }

  async clearTechnicianWorkHours(userId: string): Promise<types.TechnicianWorkHours> {
    const response = await this.http.delete<types.APIResponse<types.TechnicianWorkHours>>(`/dispatch/technicians/${userId}/work-hours`);
    return response.data.data;
  }

  async listTimeOff(query: types.TimeOffQuery = {}): Promise<types.TimeOff[]> {
    const response = await this.http.get<types.APIResponse<types.TimeOff[]>>('/dispatch/time-off', { params: query });
    return response.data.data;
  }

  async createTimeOff(data: types.CreateTimeOffRequest): Promise<types.TimeOff> {
    const response = await this.http.post<types.APIResponse<types.TimeOff>>('/dispatch/time-off', data);
    return response.data.data;
  }

  async updateTimeOff(id: string, data: types.UpdateTimeOffRequest): Promise<types.TimeOff> {
    const response = await this.http.patch<types.APIResponse<types.TimeOff>>(`/dispatch/time-off/${id}`, data);
    return response.data.data;
  }

  async deleteTimeOff(id: string): Promise<void> {
    await this.http.delete(`/dispatch/time-off/${id}`);
  }

  async listDispatchRoutes(): Promise<types.DispatchRoute[]> {
    const response = await this.http.get<types.APIResponse<types.DispatchRoute[]>>('/dispatch/routes');
    return response.data.data;
  }

  async createDispatchRoute(data: types.CreateDispatchRouteRequest): Promise<types.DispatchRoute> {
    const response = await this.http.post<types.APIResponse<types.DispatchRoute>>('/dispatch/routes', data);
    return response.data.data;
  }

  async updateDispatchRoute(id: string, data: types.UpdateDispatchRouteRequest): Promise<types.DispatchRoute> {
    const response = await this.http.patch<types.APIResponse<types.DispatchRoute>>(`/dispatch/routes/${id}`, data);
    return response.data.data;
  }

  async deleteDispatchRoute(id: string): Promise<void> {
    await this.http.delete(`/dispatch/routes/${id}`);
  }

  async getTechnicianRoutes(userId: string): Promise<string[]> {
    const response = await this.http.get<types.APIResponse<{ userId: string; routeIds: string[] }>>(`/dispatch/technicians/${userId}/routes`);
    return response.data.data.routeIds;
  }

  async setTechnicianRoutes(userId: string, routeIds: string[]): Promise<string[]> {
    const response = await this.http.put<types.APIResponse<{ userId: string; routeIds: string[] }>>(`/dispatch/technicians/${userId}/routes`, { routeIds });
    return response.data.data.routeIds;
  }

  async listRouteAssignments(query: types.RouteAssignmentQuery): Promise<types.RouteAssignment[]> {
    const response = await this.http.get<types.APIResponse<types.RouteAssignment[]>>('/dispatch/route-assignments', { params: query });
    return response.data.data;
  }

  async createRouteAssignment(data: types.CreateRouteAssignmentRequest): Promise<types.RouteAssignment> {
    const response = await this.http.post<types.APIResponse<types.RouteAssignment>>('/dispatch/route-assignments', data);
    return response.data.data;
  }

  async deleteRouteAssignment(id: string): Promise<void> {
    await this.http.delete(`/dispatch/route-assignments/${id}`);
  }

  async setRouteDefault(entity: types.RouteDefaultEntity, id: string, data: types.SetRouteDefaultRequest): Promise<types.SetRouteDefaultResult> {
    const response = await this.http.patch<types.APIResponse<types.SetRouteDefaultResult>>(`/dispatch/defaults/${entity}/${id}`, data);
    return response.data.data;
  }
}
